// Slide JSON 的 DOM 渲染器（任务 3.1）· 预览/编辑/缩略图共用
// 几何与字号 1:1 换算自后端 _shared/pptx/layouts.ts（1in = 96px，1pt = 96/72px），
// 1280×720 设计空间 + transform 缩放适配容器，保证「在线预览 ≈ 导出 pptx」。

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { LAYOUT_LABEL, METRIC_COLORS, THEME } from "../lib/slides";
import type { Block, SlideContent } from "../lib/slides";
import V2Slide from "./v2/V2Slide";

const W = 1280;
const H = 720;
const IN = 96;            // 英寸 → px
const PT = 96 / 72;       // pt → px

const px = (inch: number) => inch * IN;
const pt = (p: number) => p * PT;

function box(x: number, y: number, w: number, h: number): CSSProperties {
  return { position: "absolute", left: px(x), top: px(y), width: px(w), height: px(h) };
}

// V2 渲染共用（v2/ 组件经此同源换算，禁止另写换算函数）
export { W, H, px, pt, box };

// ---------- 可编辑文字（非受控 contentEditable，防光标跳动） ----------

export interface EditHandlers {
  // value 允许 number：数值型字段（chart value 等）持久化前 parse 回数字（045 #12）
  onEdit?: (path: string, value: string | number) => void;
  onBeginEdit?: (path: string) => void;
  onEndEdit?: (path: string) => void;
}

interface EditableTextProps extends Omit<EditHandlers, "onEdit"> {
  value: string;
  path: string;
  editable?: boolean;
  multiline?: boolean;
  style?: CSSProperties;
  // contentEditable 只产出 string，窄于 EditHandlers.onEdit（string | number）——
  // 消费侧包装函数可按 string 接收，而 handlers.onEdit 直传也兼容（宽入参可赋给窄插槽）
  onEdit?: (path: string, value: string) => void;
}

/** 非受控 contentEditable：仅在「外部值变化且未聚焦」时回写 DOM，光标不跳 */
export function EditableText({ value, path, editable, multiline, style, onEdit, onBeginEdit, onEndEdit }: EditableTextProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el && el.textContent !== value && document.activeElement !== el) {
      el.textContent = value;
    }
  }, [value]);

  return (
    <div
      ref={ref}
      style={style}
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck={false}
      data-path={path}
      onFocus={() => editable && onBeginEdit?.(path)}
      onBlur={() => editable && onEndEdit?.(path)}
      onInput={(e) => editable && onEdit?.(path, (e.target as HTMLDivElement).innerText)}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLDivElement).blur();
        }
      }}
      onPaste={(e) => {
        if (!editable) return;
        // 纯文本粘贴（防带入富文本样式，对齐原型 Bug-3 修复）
        e.preventDefault();
        const txt = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, txt);
      }}
    />
  );
}

// ---------- 各区域组件 ----------

function TopBar() {
  return (
    <>
      <div style={{ position: "absolute", left: 0, top: 0, width: W * 0.6, height: px(0.08), background: THEME.primary }} />
      <div style={{ position: "absolute", left: W * 0.6, top: 0, width: W * 0.4, height: px(0.08), background: THEME.accent }} />
    </>
  );
}

function BottomLine() {
  return <div style={{ position: "absolute", left: px(0.6), top: px(H / IN - 0.35), width: px(13.333 - 1.2), height: px(0.015), background: THEME.line }} />;
}

function PageNum({ idx }: { idx: number }) {
  return (
    <div style={{ position: "absolute", left: px(13.333 - 1.0), top: px(7.5 - 0.5), width: px(0.6), height: px(0.3), fontSize: pt(9), color: THEME.muted, textAlign: "right", fontFamily: THEME.font }}>
      {idx}
    </div>
  );
}

function TitleArea({ slide, editable, handlers }: { slide: SlideContent; editable?: boolean; handlers: EditHandlers }) {
  return (
    <>
      <div style={{ ...box(0.7, 0.35, 6, 0.3), fontSize: pt(11), color: THEME.accent, fontWeight: 700, letterSpacing: 2, fontFamily: THEME.font }}>
        {LAYOUT_LABEL[slide.layout] ?? ""}
      </div>
      <EditableText
        value={slide.title} path="title" editable={editable} {...handlers}
        style={{ ...box(0.7, 0.65, 13.333 - 1.4, 0.9), fontSize: pt(28), lineHeight: 1.15, color: THEME.primaryDark, fontWeight: 700, fontFamily: THEME.font, outline: "none" }}
      />
      <div style={{ position: "absolute", left: px(0.7), top: px(1.55), width: px(1.8), height: px(0.04), borderRadius: 2, background: THEME.primary }} />
      <EditableText
        value={slide.subtitle ?? ""} path="subtitle" editable={editable} {...handlers}
        style={{ ...box(0.7, 1.7, 13.333 - 1.4, 0.45), fontSize: pt(14), color: THEME.muted, fontFamily: THEME.font, outline: "none" }}
      />
    </>
  );
}

interface IndexedBlock { b: Block; i: number }

function MetricCards({ metrics, editable, handlers }: { metrics: IndexedBlock[]; editable?: boolean; handlers: EditHandlers }) {
  const n = Math.max(1, metrics.length);
  const gap = 0.35;
  const w = (13.333 - 1.4 - gap * (n - 1)) / n;
  const y = 2.3;
  return (
    <>
      {metrics.map(({ b, i }, k) => {
        if (b.type !== "metric") return null;
        const x = 0.7 + k * (w + gap);
        const color = METRIC_COLORS[k % METRIC_COLORS.length];
        return (
          <div key={i} style={{
            ...box(x, y, w, 1.9), background: THEME.paper, border: `1px solid ${THEME.line}`,
            borderRadius: px(0.1), boxShadow: "0 2px 4px rgba(0,0,0,.08)",
          }}>
            <div style={{ position: "absolute", left: px(0.15), top: px(0.12), width: px(w - 0.3), height: px(0.06), borderRadius: 3, background: color }} />
            <EditableText
              value={b.value} path={`blocks.${i}.value`} editable={editable} {...handlers}
              style={{ ...box(0, 0.35, w, 0.9), fontSize: pt(32), fontWeight: 700, color, textAlign: "center", lineHeight: `${px(0.9)}px`, fontFamily: THEME.font, outline: "none" }}
            />
            <EditableText
              value={b.label} path={`blocks.${i}.label`} editable={editable} {...handlers}
              style={{ ...box(0, 1.25, w, 0.5), fontSize: pt(12), color: THEME.ink, textAlign: "center", fontFamily: THEME.font, outline: "none" }}
            />
          </div>
        );
      })}
    </>
  );
}

function BulletRows({ groups, texts, y, w, editable, handlers }: {
  groups: IndexedBlock[]; texts: IndexedBlock[]; y: number; w: number;
  editable?: boolean; handlers: EditHandlers;
}) {
  const rows: { key: string; path: string; value: string; muted: boolean }[] = [];
  for (const { b, i } of groups) {
    if (b.type !== "bullets") continue;
    b.items.forEach((t, j) => rows.push({ key: `${i}.${j}`, path: `blocks.${i}.items.${j}`, value: t, muted: false }));
  }
  for (const { b, i } of texts) {
    if (b.type !== "text") continue;
    rows.push({ key: `t${i}`, path: `blocks.${i}.text`, value: b.text, muted: true });
  }
  return (
    <div style={{ ...box(0.7, y, w, 4.2), fontFamily: THEME.font }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
          <span style={{ color: r.muted ? THEME.muted : THEME.ink, opacity: 0.7, fontSize: pt(15) * 0.55, lineHeight: `${pt(15) * 1.3}px`, marginRight: 10, flex: "none" }}>●</span>
          <EditableText
            value={r.value} path={r.path} editable={editable} multiline {...handlers}
            style={{ flex: 1, fontSize: pt(15), lineHeight: 1.3, color: r.muted ? THEME.muted : THEME.ink, outline: "none" }}
          />
        </div>
      ))}
    </div>
  );
}

function ImageBox({ img, y, editable, handlers, onImageClick }: {
  img: IndexedBlock; y: number; editable?: boolean;
  handlers: EditHandlers; onImageClick?: (blockIndex: number) => void;
}) {
  if (img.b.type !== "image") return null;
  const b = img.b;
  return (
    <div
      style={{ ...box(7.5, y, 5.1, 3.8), cursor: editable ? "pointer" : "default" }}
      onClick={editable ? () => onImageClick?.(img.i) : undefined}
      title={editable ? "点击替换配图" : undefined}
    >
      <div style={{
        position: "absolute", inset: 0, borderRadius: px(0.1), background: THEME.soft,
        border: `1.5px ${b.url ? "solid" : "dashed"} ${THEME.line}`, overflow: "hidden",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        {b.url ? (
          <img src={b.url} alt={b.prompt ?? "配图"} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <>
            <div style={{ fontSize: pt(28), marginBottom: px(0.15) }}>🖼</div>
            <EditableText
              value={b.prompt ?? "配图位"} path={`blocks.${img.i}.prompt`} editable={editable} {...handlers}
              style={{ fontSize: pt(11), color: THEME.muted, textAlign: "center", fontFamily: THEME.font, outline: "none", maxWidth: "90%" }}
            />
          </>
        )}
      </div>
      {editable && (
        <div style={{
          position: "absolute", right: 8, bottom: 8, padding: "4px 10px", borderRadius: 6,
          background: "rgba(4,44,83,.75)", color: "#fff", fontSize: 12, fontFamily: THEME.font,
        }}>
          {b.url ? "替换配图" : "上传配图"}
        </div>
      )}
    </div>
  );
}

// ---------- 三大版式族 ----------

function CoverSlide({ slide, editable, handlers }: { slide: SlideContent; editable?: boolean; handlers: EditHandlers }) {
  return (
    <>
      {/* 装饰圆 */}
      <div style={{ position: "absolute", left: px(13.333 - 3.5), top: px(-1.5), width: px(5), height: px(5), borderRadius: "50%", background: THEME.primary, opacity: 0.3 }} />
      <div style={{ position: "absolute", left: px(-1.5), top: px(7.5 - 3), width: px(4), height: px(4), borderRadius: "50%", background: THEME.accent, opacity: 0.2 }} />
      {/* 底部色带 */}
      <div style={{ position: "absolute", left: 0, top: px(7.5 - 1.2), width: W, height: px(1.2), background: THEME.primary }} />
      <EditableText
        value={slide.title} path="title" editable={editable} multiline {...handlers}
        style={{ ...box(1.2, 2.2, 13.333 - 2.4, 2.0), display: "flex", alignItems: "center", fontSize: pt(44), lineHeight: 1.15, fontWeight: 700, color: THEME.paper, fontFamily: THEME.font, outline: "none" }}
      />
      <EditableText
        value={slide.subtitle ?? ""} path="subtitle" editable={editable} {...handlers}
        style={{ ...box(1.2, 4.3, 13.333 - 2.4, 0.7), fontSize: pt(18), color: THEME.coverSub, fontFamily: THEME.font, outline: "none" }}
      />
      <div style={{ ...box(1.2, 7.5 - 0.9, 5, 0.5), fontSize: pt(12), color: THEME.coverSub, fontFamily: THEME.font }}>
        策略咖 · AI 营销方案
      </div>
    </>
  );
}

function VisionSlide({ slide, editable, handlers }: { slide: SlideContent; editable?: boolean; handlers: EditHandlers }) {
  return (
    <>
      <div style={{ position: "absolute", left: px(13.333 / 2 - 3), top: px(7.5 / 2 - 3), width: px(6), height: px(6), borderRadius: "50%", background: THEME.primary, opacity: 0.25 }} />
      <div style={{ ...box(0.7, 0.5, 3, 0.35), fontSize: pt(12), color: THEME.coverSub, fontWeight: 700, letterSpacing: 2, fontFamily: THEME.font }}>
        {LAYOUT_LABEL.vision}
      </div>
      <EditableText
        value={slide.title} path="title" editable={editable} multiline {...handlers}
        style={{ ...box(1.2, 2.4, 13.333 - 2.4, 2.2), display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: pt(38), lineHeight: 1.2, fontWeight: 700, color: THEME.paper, fontFamily: THEME.font, outline: "none" }}
      />
      <EditableText
        value={slide.subtitle ?? ""} path="subtitle" editable={editable} {...handlers}
        style={{ ...box(1.2, 4.8, 13.333 - 2.4, 0.7), fontSize: pt(16), color: THEME.coverSub, textAlign: "center", fontFamily: THEME.font, outline: "none" }}
      />
      <div style={{ position: "absolute", left: px(13.333 / 2 - 1.5), top: px(7.5 - 0.8), width: px(3), height: px(0.04), borderRadius: 2, background: THEME.accent }} />
    </>
  );
}

function ContentSlide({ slide, pageIdx, editable, handlers, onImageClick }: {
  slide: SlideContent; pageIdx: number; editable?: boolean;
  handlers: EditHandlers; onImageClick?: (blockIndex: number) => void;
}) {
  const indexed: IndexedBlock[] = slide.blocks.map((b, i) => ({ b, i }));
  const metrics = indexed.filter((x) => x.b.type === "metric");
  const bulletGroups = indexed.filter((x) => x.b.type === "bullets");
  const texts = indexed.filter((x) => x.b.type === "text");
  const images = indexed.filter((x) => x.b.type === "image");

  let y = 2.3;
  if (metrics.length) y += 2.2;
  const hasImage = images.length > 0;
  const textW = hasImage ? 6.4 : 13.333 - 1.4;

  return (
    <>
      <TopBar />
      <TitleArea slide={slide} editable={editable} handlers={handlers} />
      {metrics.length > 0 && <MetricCards metrics={metrics} editable={editable} handlers={handlers} />}
      <BulletRows groups={bulletGroups} texts={texts} y={y} w={textW} editable={editable} handlers={handlers} />
      {hasImage && <ImageBox img={images[0]} y={y} editable={editable} handlers={handlers} onImageClick={onImageClick} />}
      <BottomLine />
      <PageNum idx={pageIdx} />
    </>
  );
}

// ---------- 主组件 ----------

export interface SlideCanvasProps extends EditHandlers {
  slide: SlideContent;
  pageIdx: number;
  editable?: boolean;
  onImageClick?: (blockIndex: number) => void;
  /** V2 视觉升级：传 style（swiss/magazine）即走 V2 渲染；缺省或 'legacy' 走旧分支 */
  style?: string | null;
  themeId?: string | null;
  /** V2 chrome 需要总页数 */
  totalPages?: number;
}

export default function SlideCanvas({ slide, pageIdx, editable, onEdit, onBeginEdit, onImageClick, style, themeId, totalPages }: SlideCanvasProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isV2 = !!style && style !== "legacy";
  const dark = slide.layout === "cover" || slide.layout === "vision";
  const handlers: EditHandlers = { onEdit, onBeginEdit };

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative", width: "100%", aspectRatio: "16 / 9", overflow: "hidden",
        background: isV2 ? undefined : dark ? THEME.primaryDark : THEME.paper, borderRadius: 4,
      }}
    >
      <div style={{ position: "absolute", left: 0, top: 0, width: W, height: H, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
        {isV2 ? (
          <V2Slide
            slide={slide} page={pageIdx} total={totalPages ?? 12}
            themeId={themeId ?? "swiss-ikb"}
            editable={editable} handlers={handlers} onImageClick={onImageClick}
          />
        ) : (
          <>
            {slide.layout === "cover" && <CoverSlide slide={slide} editable={editable} handlers={handlers} />}
            {slide.layout === "vision" && <VisionSlide slide={slide} editable={editable} handlers={handlers} />}
            {slide.layout !== "cover" && slide.layout !== "vision" && (
              <ContentSlide slide={slide} pageIdx={pageIdx} editable={editable} handlers={handlers} onImageClick={onImageClick} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
